<template>
  <div
    v-if="state"
    class="searchbox"
  >
    <q-input
      v-model="currentQuery"
      class="searchbox--input"
      :class="{ 'has-value': currentQuery }"
      type="text"
      name="search-input"
      autocomplete="off"
      :placeholder="placeholder"
      :autofocus="autofocus"
      :loading="isLoading || state.isSearchStalled"
      :debounce="debounce"
      hide-bottom-space
      dense
      outlined
      rounded
      no-error-icon
      @focus="e => emit('focus', e)"
      @blur="e => emit('blur', e)"
      @clear="emit('clear')"
    >
      <template
        v-for="(slot, slotName) in $slots"
        #[slot]="scope"
        :key="slotName"
      >
        <slot
          :name="slotName"
          v-bind="scope || {}"
        />
      </template>

      <template #loading>
        <slot name="loading-indicator" />
      </template>
    </q-input>

    <div
      v-if="statsState && currentQuery"
      class="found-items"
    >
      <slot
        name="found-items"
        v-bind="statsState"
      >
        {{ t('Stats.countPlain.results', { type: 'brands', count: statsState.nbHits }) }}
      </slot>
    </div>

    <FacetsStatsMenu
      v-if="statsState && statsState.nbHits"
      :facets-stats="statsState?.instantSearchInstance?.helper?.lastResults?.facets_stats"
    />
  </div>
</template>

<script setup>
import { watch } from 'vue'
import { useRoute } from 'vue-router'
import { useTranslation } from 'i18next-vue'
import { connectSearchBox, connectStats } from 'instantsearch.js/es/connectors'// eslint-disable-line
import { useConnector } from '@/composables'
import FacetsStatsMenu from '@/components/filters/FacetsStatsMenu.vue'

const currentQuery = defineModel('currentQuery', { type: String, default: null })
const emit = defineEmits(['update:modelValue', 'focus', 'blur', 'clear'])
defineOptions({ name: 'InstantSearchBox' })

const { t } = useTranslation('instantsearch')
const props = defineProps({
  placeholder: { type: String, default: '' },
  autofocus: { type: Boolean, default: true },
  isLoading: { type: Boolean, default: false },
  debounce: { type: Number, default: 300 },
  submitTitle: { type: String, default: 'Submit the search query' },
  resetTitle: { type: String, default: 'Clear the search query' },
  value: { type: String, default: undefined },
  queryHook: { type: Function, default: undefined }
})

const $route = useRoute()
const state = useConnector(connectSearchBox, { $$widgetType: 'ais.searchBox' }, { skipFirstRender: false })
const statsState = useConnector(connectStats, { $$widgetType: 'ais.stats' })

// Initialize input from URL for direct loads / refreshes (IS isn't ready yet)
currentQuery.value = $route.query?.q || props.value || undefined

// Keep IS in sync with input changes
watch(() => currentQuery.value, val => {
  state.value?.refine(val ?? props.value ?? '')
})

// When the IS widget first becomes available, sync it with the current input value
// (useConnector defers the first render until IS.start() has run)
watch(state, (s, prev) => {
  if (s && !prev) s.refine(currentQuery.value ?? props.value ?? '')
})

// Reflect external query changes (e.g. clearing it from the filter cloud) back into the input
watch(() => state.value?.query, query => {
  if (query === undefined || query === (currentQuery.value ?? '')) return
  currentQuery.value = query || undefined
})

</script>

<style scoped>
@reference "#app-styles.css";

.searchbox {
  @apply flex items-center gap-2;
  /* @apply w-full max-w-full; */
  @apply relative;

  .searchbox--input {
    @apply min-w-0 w-full max-w-60;
    @apply md:w-60;

    &:deep(.q-field__native),
    &:deep(.q-field__prefix),
    &:deep(.q-field__suffix),
    &:deep(.q-field__input) {
      /* @apply text-accent-mid; */
    }

    /* &:deep(.q-field__control) { @apply before:content-none after:content-none; } */
    &:deep(.q-field__before) { @apply pr-2!; }
    &:deep(.q-field__before .q-icon) { @apply transition-colors duration-300 ease-out; }

    &.has-value :deep(.q-field__before .q-icon),
    &.q-field--highlighted :deep(.q-field__before .q-icon) {
      @apply text-primary-mid;
    }

    &:deep(.q-field__before .q-icon) {
      @apply text-ink-soft/80 cursor-pointer;
      @apply transition-opacity duration-300 ease-out;
      @apply hover:ink-soft;
    }
  }

  .found-items {
    @apply text-ink-subtle text-[12px];
  }
}
</style>
